import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { postData } from '../../../utils/postData';
import StepWizard from '../../shared/StepWizard';
import GoBackRedirect from '../../shared/GoBackRedirect';
import {
    serverConstants,
    paths,
    PROGRESS_BAR_DONE_DELAY,
} from '../../../constants';
import {
    ValidateDeviceContext,
    ValidateDeviceDispatchContext,
} from './context';
import ValidateDeviceCard from '../../cardLibrary/ValidateDevice';
import PerformAction from '../../cardLibrary/PerformAction';
import {
    AppStateContext,
    AppDispatchContext,
} from '../../../context/AppContext';
import Status from '../../cardLibrary/Status';
import { HelpDeskDispatchContext } from '../../../context/HelpDeskContext';

const VALIDATE_DEVICE = 'validateDevice';
const PERFORM_ACTION = 'performAction';
const STATUS = 'status';
const HOME = 'home';

function reducer(state, action) {
    switch (action.type) {
        case 'DONE':
            return { step: HOME };
        case 'SET_PASS':
            return {
                ...state,
                pass: action.payload,
                disableNext: action.payload === '',
            };
        case 'ON_SUCCESS':
            return {
                ...state,
                statusMessage: action.payload,
                errorMessage: false,
                loading: false,
                step: STATUS,
            };
        case 'ON_FAILURE':
            return {
                ...state,
                loading: false,
                statusMessage: action.payload,
                errorMessage: true,
                step: STATUS,
            };
        case 'GO_BACK':
            if (state.step === VALIDATE_DEVICE) {
                return { ...state, step: HOME };
            }
            return state;
        case 'GO_NEXT':
            if (state.step === VALIDATE_DEVICE) {
                return { ...state, step: PERFORM_ACTION, loading: true };
            }
            return state;
        case 'PROGRESS_BAR_DONE':
            return { ...state, isDone: true };
        default:
            throw new Error('Validate Device Reducer: invalid action');
    }
}

const ValidateDevice = ({withPin = true}) => {
    const appDispatch = useContext(AppDispatchContext);
    const { device, goBackLink } = useContext(AppStateContext);
    const helpDeskDispatch = useContext(HelpDeskDispatchContext);
    const [state, dispatch] = useReducer(reducer, {
        step: VALIDATE_DEVICE,
        nextStep: PERFORM_ACTION,
        prevStep: HOME,
        disableNext: true,
        pass: '',
        statusMessage: null,
        errorMessage: false,
        loading: false,
        device,
        isDone: false,
    });
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const { pathname } = location || {};
    const isHelpDesk = pathname ? pathname.includes(paths.helpDesk) : false;
    const returnPath =
        goBackLink || (isHelpDesk ? paths.helpDesk : paths.identities);

    const performAction = useCallback(async () => {
        const body = { pass: state.pass };
        body.user = state.device.user ? state.device.user : undefined;
        const queryParams = state.device.subType ? {subType: state.device.subType} : {};
        if (!withPin) {
            body.otpOnly = '1';
        } 
        const res = await postData({
            body,
            endpoint: `/device/validateDevice/${
                state.device.subType || state.device.type
            }/${state.device.serial}`,
            appDispatch,
            queryParams
        });
        const content = await res.clone().text();
        if (content) {
            const json = await res.json();
            if (json.result === serverConstants.success) {
                dispatch({ type: 'PROGRESS_BAR_DONE' });
                setTimeout(
                    () =>
                        dispatch({
                            type: 'ON_SUCCESS',
                            payload: 'validateDeviceSuccessMsg',
                        }),
                    PROGRESS_BAR_DONE_DELAY,
                );
            } else {
                dispatch({ type: 'PROGRESS_BAR_DONE' });
                setTimeout(
                    () =>
                        dispatch({
                            type: 'ON_FAILURE',
                            payload: `errorMsgs$${json.message}`,
                        }),
                    PROGRESS_BAR_DONE_DELAY,
                );
            }
        } else {
            dispatch({ type: 'PROGRESS_BAR_DONE' });
            setTimeout(
                () =>
                    dispatch({
                        type: 'ON_FAILURE',
                        payload: t('errorMsgs$serverDefault'),
                    }),
                PROGRESS_BAR_DONE_DELAY,
            );
        }
        helpDeskDispatch({ type: 'RESET_CONTEXT' });
    }, [appDispatch, helpDeskDispatch, state.device.serial, state.device.subType, state.device.type, state.device.user, state.pass, t]);

    useEffect(() => {
        if (state.step === PERFORM_ACTION) {
            performAction();
        }
    }, [state.step, performAction]);

    useEffect(() => {
        document.addEventListener('keyup', handleEnter);
        return () => {
            helpDeskDispatch({ type: 'RESET_CONTEXT' });
            appDispatch({ type: 'RESET_FLOWS' });
            document.removeEventListener('keyup', handleEnter);
        };
    },[]);

    const handleEnter = (e) => {
        if (e.keyCode === 13) {
            dispatch({type: 'GO_NEXT'})
        }
    };
    if (!device) {
        return <Navigate to={returnPath} replace />;
    }

    return (
        <ValidateDeviceContext.Provider value={state}>
            <ValidateDeviceDispatchContext.Provider value={dispatch}>
                <StepWizard
                    step={state.step}
                    title={t('cardTitles$validateDevice')}
                    dispatch={dispatch}
                    state={state}
                >
                    <GoBackRedirect
                        path={returnPath}
                        stepId={HOME}
                    />
                    <ValidateDeviceCard
                        stepId={VALIDATE_DEVICE}
                        dispatch={dispatch}
                        device={state.device}
                        withPin={withPin}
                    />
                    <PerformAction
                        stepId={PERFORM_ACTION}
                        isDone={state.isDone}
                    />
                    <Status path={returnPath} stepId={STATUS} state={state} />
                </StepWizard>
            </ValidateDeviceDispatchContext.Provider>
        </ValidateDeviceContext.Provider>
    );
};

export default ValidateDevice;
